"use client";

import React from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { motion, useScroll, useTransform } from "framer-motion";

import { PageMentoringV2Schema } from "@/sanity/pages/PageMentoringV2Template";

const fadeInUp = (delay?: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function GlanceSection({ data, className }: { data: PageMentoringV2Schema; className?: string }) {
  const lineContainerRef = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: lineContainerRef,
    offset: ["start end", "end center"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section className={cn("relative overflow-clip bg-[#0A1A1D] pt-[8rem] lg:pt-[16rem]", className)}>
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="right"
        style="light"
      />

      <div className="container flex flex-col items-center">
        <div className="w-[min(187.75rem,100%)] flex flex-col lg:flex-row items-start gap-[4rem] lg:gap-[8rem]  justify-between">
          {/* Left: Chip + Title */}
          <div className="flex flex-col items-start lg:sticky lg:top-[8rem]">
            {data?.glanceChip && (
              <motion.div {...fadeInUp()}>
                <Chip
                  image={data.glanceChip.icon}
                  text={data.glanceChip.text}
                />
              </motion.div>
            )}

            {data?.glanceTitle && (
              <motion.div
                {...fadeInUp(0.1)}
                className="richtext richtext-start prose-headings:text-white prose-headings:font-ivy-headline-regular prose-headings:tracking-[0.075rem] lg:prose-headings:text-[7rem] prose-headings:leading-[100%] prose-headings:text-[5rem]">
                <PortableText
                  value={data.glanceTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
          </div>

          {/* Right: Items */}
          {data?.glanceItems && data.glanceItems.length > 0 && (
            <div
              ref={lineContainerRef}
              className="w-[min(96.5rem,100%)] flex flex-col gap-[4rem] relative">
              {/* White base line */}
              <div className="absolute lg:left-[10.65%] h-full w-[.125rem] bg-white/5 left-[13.25%]" />
              {/* Green overlay that fills on scroll */}
              <motion.div
                className="absolute lg:left-[10.65%] w-[.125rem] bg-[#D9F9B9] origin-top left-[13.25%]"
                style={{ height: lineHeight }}
              />
              {data.glanceItems.map((item, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp(0.1 + 0.08 * index)}
                  className="flex relative items-center w-full lg:gap-0 lg:justify-between gap-[2rem]">
                  <div className="flex justify-center h-[3rem] bg-[#D9F9B9] w-[5.5rem] lg:h-fit lg:pb-[.5rem] lg:w-[8rem]">
                    <p className="m-0 text-[#0A1A1D] text-[2rem] lg:text-[2.25rem]">{item.step}</p>
                  </div>

                  <div className="w-[.625rem] h-[0.625rem] bg-[#D9F9B9] shrink-0" />

                  <div className="flex lg:items-center lg:flex-row flex-col bg-white/5 lg:p-[3rem] lg:gap-[3rem] w-[min(83.875rem,100%)] p-[2rem] ">
                    {item?.icon && (
                      <div className="flex flex-shrink-0 justify-center items-center w-[6rem] h-[9rem]">
                        <Image
                          className="w-full h-auto"
                          src={item.icon}
                          alt=""
                          width={item.icon.width}
                          height={item.icon.height}
                        />
                      </div>
                    )}

                    {item?.text && (
                      <div className="richtext richtext-start lg:w-auto -mt-[2rem] lg:mt-0 prose-h4:font-[500] prose-h4:text-[2.5rem] prose-h4:!w-full prose-h4:text-white prose-h4:font-geist prose-p:text-white/80 prose-p:m-0 prose-p:w-[80%]">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
